// Shared UI primitives — Card, Button, Modal, LoadingSpinner, EmptyState, HudCorners.
// One file: each is a handful of lines and they always travel together.
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export function Card({ children, className = '' }) {
  return <div className={`card ${className}`}>{children}</div>;
}

// L-shaped HUD frame corners — drop inside any relatively-positioned panel
// (Card is position:relative already). Pair with the `hud-frame` class on
// the parent to brighten the brackets on hover.
export function HudCorners() {
  return (
    <>
      <span className="hud-corner hud-corner--tl" aria-hidden="true" />
      <span className="hud-corner hud-corner--tr" aria-hidden="true" />
      <span className="hud-corner hud-corner--bl" aria-hidden="true" />
      <span className="hud-corner hud-corner--br" aria-hidden="true" />
    </>
  );
}

export function Button({ children, variant = 'primary', className = '', ...props }) {
  const variants = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    danger: 'btn-danger',
  };
  return (
    <button
      type="button"
      className={`${variants[variant] || variants.primary} min-h-[44px] ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function LoadingSpinner({ size = 'md', className = '' }) {
  const sizes = { sm: 'w-4 h-4', md: 'w-8 h-8', lg: 'w-12 h-12' };
  return (
    <div className={`flex items-center justify-center py-6 ${className}`} role="status" aria-label="Loading">
      <div
        className={`${sizes[size] || sizes.md} border-2 border-primary-400/25 border-t-primary-300 rounded-full animate-spin shadow-[0_0_16px_rgba(34,211,238,0.35)]`}
      />
    </div>
  );
}

export function EmptyState({ icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-10 px-4">
      {icon && <div className="text-4xl mb-3 opacity-80 [filter:drop-shadow(0_0_14px_rgba(34,211,238,0.35))]" aria-hidden="true">{icon}</div>}
      <h3 className="font-display font-bold uppercase tracking-[0.14em] text-sm text-night-text">{title}</h3>
      {description && <p className="text-sm text-night-muted mt-2 max-w-xs">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Modal({ isOpen, onClose, title, children }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  // Portal to <body>: ancestors with backdrop-filter (e.g. .card) become the
  // containing block for position:fixed, so a nested modal gets clipped to the
  // card's overflow:hidden box (App Review: "IAP page is cropped" on iPad).
  return createPortal(
    <div
      className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm safe-area-top safe-area-bottom"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className="relative overflow-hidden w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-[85vh] overflow-y-auto
                   bg-[rgba(10,28,46,0.92)] border border-primary-400/25 backdrop-blur-xl
                   shadow-[0_0_60px_-15px_rgba(34,211,238,0.4)]
                   before:content-[''] before:absolute before:top-0 before:left-[10%] before:right-[10%] before:h-px
                   before:bg-gradient-to-r before:from-transparent before:via-primary-300 before:to-transparent before:opacity-60"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 id="modal-title" className="text-base font-display font-bold uppercase tracking-[0.2em] text-primary-300">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-night-muted hover:text-primary-300 text-xl px-2 transition-colors">
            ×
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}
